import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allArticles } from '@/mocks/article';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = allArticles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <main className="pt-20 md:pt-24 pb-14 md:pb-20 px-4 md:px-6 bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-300 mb-4 block" />
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接无效</p>
          <Link
            to="/category"
            className="inline-block px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回内容列表
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <main className="pt-20 md:pt-24 pb-14 md:pb-20 px-4 md:px-6 bg-background-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '分类浏览', path: '/category' },
              { label: article.category, path: `/category?tab=${article.categoryId}` },
              { label: article.title },
            ]}
          />
        </div>

        <article>
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs font-medium whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                <span>{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                <time dateTime={article.date}>{article.date}</time>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                <span>{article.views} 阅读</span>
              </div>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-64 md:h-80 lg:h-96 object-cover object-top"
              title={`${article.title} - 蜜桃传媒精品内容`}
            />
          </div>

          <div className="prose-custom text-foreground-700 leading-relaxed text-sm md:text-base">
            {article.content?.split('\n').map((paragraph, idx) => (
              <p key={idx} className="mb-5">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                {[
                  { icon: 'ri-weibo-line', label: '微博' },
                  { icon: 'ri-wechat-line', label: '微信' },
                  { icon: 'ri-link', label: '复制链接' },
                ].map((share) => (
                  <button
                    key={share.label}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                    aria-label={share.label}
                  >
                    <i className={`${share.icon} text-base`} />
                  </button>
                ))}
              </div>
              <Link
                to="/category"
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                &larr; 返回列表
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="mt-14 md:mt-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group block cursor-pointer"
                >
                  <article className="bg-background-100 rounded-lg overflow-hidden transition-transform duration-300 group-hover:-translate-y-1">
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={item.cover}
                        alt={item.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        title={`${item.title} - 蜜桃传媒`}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-1 group-hover:text-primary-500 transition-colors">
                        {item.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{item.date}</span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}